if (!customElements.get('quiz-overlay')) {
  customElements.define(
    'quiz-overlay',
    class QuizOverlay extends HTMLElement {
      static STORAGE_KEY = 'quizDismissed';
      static FLOW = ['q1', 'q2', 'q3', 'q4', 'validation', 'q5', 'results', 'product'];
      static PROGRESS_STEPS = { q1: 1, q2: 2, q3: 3, q4: 4, validation: 4, q5: 5, results: 5, product: 5 };
      static SINGLE_SELECT_DELAY = 650;
      static LOADING_DELAY = 900;
      static VALIDATION_DELAY = 2500;
      static RESULTS_DELAY = 2500;

      constructor() {
        super();
        this.answers = {};
        this.advanceTimer = null;
        this.handleTrapKeydown = this.handleTrapKeydown.bind(this);
      }

      connectedCallback() {
        this.panel = this.querySelector('.quiz-overlay__panel');
        this.closeButton = this.querySelector('.quiz-overlay__close');
        this.backButton = this.querySelector('.quiz-overlay__back');
        this.loadingEl = this.querySelector('.quiz-loading');
        this.progressSteps = Array.from(this.querySelectorAll('.quiz-progress__step'));
        this.steps = Array.from(this.querySelectorAll('.quiz-step'));
        this.resultRows = {};
        this.querySelectorAll('.quiz-results-card__row').forEach((row) => {
          this.resultRows[row.dataset.resultRow] = {
            icon: row.querySelector('.quiz-results-card__icon'),
            text: row.querySelector('.quiz-results-card__text'),
          };
        });
        this.validationVariants = Array.from(this.querySelectorAll('.quiz-validation__text'));

        this.closeButton.addEventListener('click', () => this.close(true));
        if (this.backButton) this.backButton.addEventListener('click', () => this.goBack());
        this.addEventListener('keyup', (event) => {
          if (event.code === 'Escape') this.close(true);
        });

        this.querySelectorAll('.quiz-step[data-type="single"] .quiz-option').forEach((option) => {
          option.addEventListener('click', () => this.handleSingleSelect(option));
        });

        this.querySelectorAll('.quiz-step[data-type="multi"] .quiz-option').forEach((option) => {
          option.addEventListener('click', () => this.handleMultiToggle(option));
        });

        this.querySelectorAll('.quiz-continue').forEach((button) => {
          button.addEventListener('click', () => this.goNext());
        });

        if (window.localStorage.getItem(QuizOverlay.STORAGE_KEY) !== 'true') {
          this.open();
        }
      }

      disconnectedCallback() {
        this.clearAdvanceTimer();
        document.removeEventListener('keydown', this.handleTrapKeydown);
      }

      open() {
        this.setAttribute('open', '');
        document.body.classList.add('quiz-overlay-active');
        document.addEventListener('keydown', this.handleTrapKeydown);
        this.showStep(QuizOverlay.FLOW[0]);
      }

      close(setFlag) {
        this.clearAdvanceTimer();
        if (setFlag) window.localStorage.setItem(QuizOverlay.STORAGE_KEY, 'true');
        this.removeAttribute('open');
        document.body.classList.remove('quiz-overlay-active');
        document.removeEventListener('keydown', this.handleTrapKeydown);
      }

      // Focusable elements exist across every step at once (only one is visible),
      // so the set of tab stops is recomputed on every keypress rather than cached.
      handleTrapKeydown(event) {
        if (event.code !== 'Tab' || !this.hasAttribute('open')) return;

        const focusable = getFocusableElements(this).filter((element) => element.offsetParent !== null);
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      clearAdvanceTimer() {
        if (this.advanceTimer) {
          clearTimeout(this.advanceTimer);
          this.advanceTimer = null;
        }
      }

      currentStepKey() {
        const activeStep = this.steps.find((step) => step.classList.contains('is-active'));
        return activeStep ? activeStep.dataset.step : QuizOverlay.FLOW[0];
      }

      showStep(stepKey) {
        let activeStep = null;
        this.steps.forEach((step) => {
          const isActive = step.dataset.step === stepKey;
          step.classList.toggle('is-active', isActive);
          if (isActive) activeStep = step;
        });
        this.updateProgress(stepKey);

        if (this.backButton) {
          this.backButton.hidden = QuizOverlay.FLOW.indexOf(stepKey) === 0;
          this.backButton.disabled = false;
        }

        if (stepKey === 'validation') this.updateValidationCopy();

        if (activeStep) activeStep.focus({ preventScroll: true });

        if (stepKey === 'validation') {
          this.clearAdvanceTimer();
          this.advanceTimer = setTimeout(() => this.goNext(), QuizOverlay.VALIDATION_DELAY);
        } else if (stepKey === 'results') {
          this.renderResults();
          window.localStorage.setItem(QuizOverlay.STORAGE_KEY, 'true');
          this.clearAdvanceTimer();
          this.advanceTimer = setTimeout(() => this.goNext(), QuizOverlay.RESULTS_DELAY);
        }
      }

      updateProgress(stepKey) {
        const filledCount = QuizOverlay.PROGRESS_STEPS[stepKey] || 0;
        const isCurrentQuestion = /^q[1-5]$/.test(stepKey);

        this.progressSteps.forEach((step, index) => {
          const stepNumber = index + 1;
          step.classList.toggle('is-filled', stepNumber <= filledCount);
          step.classList.toggle('is-current', isCurrentQuestion && stepNumber === filledCount);
        });
      }

      // Shows a brief "personalizing" transition whenever leaving a real question,
      // so answering feels like it's actively shaping what comes next.
      goNext() {
        this.clearAdvanceTimer();
        const currentStep = this.steps.find((step) => step.classList.contains('is-active'));
        const currentKey = currentStep ? currentStep.dataset.step : QuizOverlay.FLOW[0];
        const nextKey = QuizOverlay.FLOW[QuizOverlay.FLOW.indexOf(currentKey) + 1];
        if (!nextKey) return;

        const isQuestion = currentStep && (currentStep.dataset.type === 'single' || currentStep.dataset.type === 'multi');
        if (isQuestion) {
          this.showLoading(() => this.showStep(nextKey));
        } else {
          this.showStep(nextKey);
        }
      }

      // Walks back past non-question interstitials (validation, results) so
      // "back" always lands on the nearest question the user can actually edit.
      goBack() {
        this.clearAdvanceTimer();
        let index = QuizOverlay.FLOW.indexOf(this.currentStepKey()) - 1;

        while (index >= 0) {
          const key = QuizOverlay.FLOW[index];
          const step = this.steps.find((candidate) => candidate.dataset.step === key);
          if (step && (step.dataset.type === 'single' || step.dataset.type === 'multi')) {
            this.showStep(key);
            return;
          }
          index -= 1;
        }
      }

      showLoading(callback) {
        if (this.backButton) this.backButton.disabled = true;
        this.steps.forEach((step) => step.classList.remove('is-active'));

        if (this.loadingEl) {
          this.loadingEl.classList.add('is-active');
          this.loadingEl.focus({ preventScroll: true });
        }

        this.clearAdvanceTimer();
        this.advanceTimer = setTimeout(() => {
          if (this.loadingEl) this.loadingEl.classList.remove('is-active');
          callback();
        }, QuizOverlay.LOADING_DELAY);
      }

      handleSingleSelect(option) {
        const step = option.closest('.quiz-step');
        const question = step.dataset.step;

        step.querySelectorAll('.quiz-option').forEach((sibling) => {
          const isSelected = sibling === option;
          sibling.classList.toggle('is-selected', isSelected);
          sibling.setAttribute('aria-pressed', String(isSelected));
        });

        this.answers[question] = {
          value: option.dataset.value,
          label: option.querySelector('.quiz-option__label').textContent,
          icon: option.querySelector('.quiz-option__icon').textContent,
        };

        this.clearAdvanceTimer();
        this.advanceTimer = setTimeout(() => this.goNext(), QuizOverlay.SINGLE_SELECT_DELAY);
      }

      handleMultiToggle(option) {
        const step = option.closest('.quiz-step');
        const question = step.dataset.step;
        const options = Array.from(step.querySelectorAll('.quiz-option'));
        const isExclusive = option.dataset.exclusive === 'true';
        const willSelect = !option.classList.contains('is-selected');

        if (isExclusive) {
          options.forEach((sibling) => this.setOptionSelected(sibling, sibling === option && willSelect));
        } else {
          this.setOptionSelected(option, willSelect);
          const exclusiveOption = options.find((sibling) => sibling.dataset.exclusive === 'true');
          if (exclusiveOption) this.setOptionSelected(exclusiveOption, false);
        }

        const selected = options.filter((sibling) => sibling.classList.contains('is-selected'));
        this.answers[question] = selected.map((sibling) => ({
          value: sibling.dataset.value,
          label: sibling.querySelector('.quiz-option__label').textContent,
          tag: sibling.dataset.tag,
        }));

        const continueButton = step.querySelector('.quiz-continue');
        if (continueButton) {
          const wasDisabled = continueButton.disabled;
          const isNowEnabled = selected.length > 0;
          continueButton.disabled = !isNowEnabled;

          if (wasDisabled && isNowEnabled) {
            continueButton.classList.remove('quiz-continue--pulse');
            void continueButton.offsetWidth; // restart the animation if it fires again
            continueButton.classList.add('quiz-continue--pulse');
          }
        }
      }

      setOptionSelected(option, selected) {
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-pressed', String(selected));
      }

      updateValidationCopy() {
        const q4 = this.answers.q4 || [];
        const triedNothing = q4.length === 1 && q4[0].value === 'nothing';
        const variant = triedNothing ? 'nothing' : 'default';

        this.validationVariants.forEach((el) => {
          el.hidden = el.dataset.validationVariant !== variant;
        });
      }

      renderResults() {
        const q1 = this.answers.q1;
        const q2 = this.answers.q2;
        const q2Value = q2 ? q2.label.charAt(0).toLowerCase() + q2.label.slice(1) : '';
        const q4Tags = (this.answers.q4 || []).map((answer) => answer.tag).filter(Boolean);
        const tried = q4Tags.length ? q4Tags.join(', ') : 'nothing yet';

        this.setResultRow('feeling', q1 && q1.icon, 'Feeling', q1 && q1.label);
        this.setResultRow('hotspot', q2 && q2.icon, 'Hits hardest', q2Value);
        this.setResultRow('tried', '📋', 'Tried', tried);
      }

      setResultRow(key, icon, labelOrText, value) {
        const row = this.resultRows[key];
        if (!row) return;

        row.icon.textContent = icon || '';

        if (value === undefined) {
          row.text.textContent = labelOrText || '';
        } else {
          row.text.innerHTML = '';
          const label = document.createElement('span');
          label.className = 'quiz-results-card__label';
          label.textContent = `${labelOrText}: `;
          row.text.append(label, value);
        }
      }
    }
  );
}
